import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/** Page-supplied rail content slot. Pages render `<PageRail>` to override
 *  the default `<PortalRail>` tabs. Single slot, last-write-wins. */

interface RailContentContextValue {
  content: ReactNode | null;
  setContent: (next: ReactNode | null) => void;
}

const RailContentContext = createContext<RailContentContextValue | null>(null);

export function RailContentProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<ReactNode | null>(null);
  // Memoize so unchanged renders don't re-render every consumer.
  const value = useMemo(() => ({ content, setContent }), [content]);
  return <RailContentContext.Provider value={value}>{children}</RailContentContext.Provider>;
}

/** Returns null when no page has called `<PageRail>`. */
export function useRailContent(): ReactNode | null {
  const ctx = useContext(RailContentContext);
  if (!ctx) return null;
  return ctx.content;
}

/** Renders children into the shared rail column. Consumers MUST memoize
 *  `children` (via `useMemo`) — a fresh JSX reference each render fires
 *  setContent every render and causes a re-render storm. */
export function PageRail({ children }: { children: ReactNode }) {
  const ctx = useContext(RailContentContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.setContent(children);
    return () => {
      ctx.setContent(null);
    };
  }, [ctx, children]);
  return null;
}
